<template>
  <div
    v-if="!leadsAvailable && leadsLoaded"
    class="one-whole h-full flex flex--center flex--justify-center pt-large"
  >
    <ak-loading-spinner />
  </div>
  <div v-if="!leadsAvailable && !leadsLoaded" class="flex__item empty-state my">
    <div class="empty-state__icon">
      <i class="icon--large icon-pie-graph" />
    </div>
    <h3 class="color-grey empty-state__title mb-tiny">
      Track projects at a glance!
    </h3>
    <p>
      You'll be able to check out your projects performance at a glance here
      once you've started capturing {{ leadsBrandingPlural }}.
    </p>
  </div>
  <div v-if="leadsAvailable && !leadsLoaded">
    <div class="one-whole flex flex--wrap">
      <div :class="size === 'large' ? 'one-third' : 'one-half'">
        <h6 class="color-grey">Project Breakdown</h6>
        <ak-card>
          <ak-card-header class="bg-secondary py-small px">
            <router-link
              :to="{
                name: 'campaignLeads',
                params: { campaignId: selectedProject._id },
              }"
              class="color-white text-bold text-link"
              >{{ overview.totalLeads ? overview.totalLeads : 0 }}
              {{
                overview.totalLeads === 1
                  ? leadsBrandingSingle
                  : leadsBrandingPlural
              }}</router-link
            >
          </ak-card-header>
          <ak-card-body class="p-0 flex flex--column">
            <ak-box class="bg-grey-lighter one-whole py-tiny">
              <div class="flex flex--space-evenly color-grey">
                <router-link
                  :to="{
                    name: 'appsListCampaign',
                    params: {
                      type: 'all',
                      cat: 'pages',
                      campaignId: selectedProject._id,
                    },
                  }"
                >
                  <ak-tooltip
                    :placement="'right'"
                    :y-axis="-20"
                    :has-arrow="false"
                  >
                    <template #trigger>
                      <div class="flex flex--center mr-small">
                        <i class="icon-editor-banner icon--big mr-tiny" />
                        <p class="mb-0 text-bold">{{ statsSummary.pages }}</p>
                      </div>
                    </template>
                    Pages
                  </ak-tooltip>
                </router-link>
                <router-link
                  :to="{
                    name: 'appsListCampaign',
                    params: {
                      type: 'all',
                      cat: 'forms',
                      campaignId: selectedProject._id,
                    },
                  }"
                >
                  <ak-tooltip
                    :placement="'right'"
                    :y-axis="-20"
                    :has-arrow="false"
                  >
                    <template #trigger>
                      <div class="flex flex--center mr-small">
                        <i class="icon-editor-form icon--big mr-tiny" />
                        <p class="mb-0 text-bold">{{ statsSummary.forms }}</p>
                      </div>
                    </template>
                    Forms
                  </ak-tooltip>
                </router-link>
                <router-link
                  :to="{
                    name: 'eventsList',
                    params: { campaignId: selectedProject._id },
                  }"
                >
                  <ak-tooltip
                    :placement="'right'"
                    :y-axis="-5"
                    :has-arrow="false"
                  >
                    <template #trigger>
                      <div class="flex flex--center">
                        <i class="icon-calendar icon--big mr-tiny" />
                        <p class="mb-0 text-bold">
                          {{ statsSummary.captures }}
                        </p>
                      </div>
                    </template>
                    Events
                  </ak-tooltip>
                </router-link>
              </div>
            </ak-box>
            <ak-box class="bg-grey-lightest one-whole px-small">
              <p class="text-small mb-tiny">
                Live for
                <strong>{{
                  formatDateSansAgo(selectedProject.createdOn)
                }}</strong>
              </p>
              <p class="text-small mb-tiny">
                First lead
                <br />
                <strong>{{ formatDate(overview.firstLeadTime) }}</strong>
              </p>
              <p class="text-small mb-tiny">
                Last lead
                <br />
                <strong>{{ formatDate(overview.lastLeadTime) }}</strong>
              </p>
              <p class="text-small mb-tiny">
                Avg. {{ leadsBrandingPlural }} Per Day
                <span>
                  <ak-tooltip :placement="'right'">
                    <template #trigger>
                      <i class="icon-help-circled color-grey-light" />
                    </template>
                    Days on which at least 1 {{ leadsBrandingSingle }} was
                    recorded
                  </ak-tooltip>
                </span>
                <br />
                <strong>{{ overview.dailyAverage }}</strong>
              </p>
              <p class="text-small mb-tiny">
                Busiest Day of Week
                <br />
                <strong>{{ indexedDayToName(overview.busiestDay) }}</strong>
              </p>
              <p class="text-small mb-tiny">
                Busiest Time of Day
                <br />
                <strong>{{ hourToRange(overview.busiestHour) }}</strong>
              </p>
            </ak-box>
          </ak-card-body>
        </ak-card>
      </div>
      <div
        :class="size === 'large' ? 'one-third pl-large' : 'one-half pl-small'"
      >
        <h6 class="color-grey">Last 3 {{ leadsBrandingPlural }}</h6>
        <div v-for="lead in overview.lastLeads" :key="lead._id">
          <div class="mb-small">
            <p class="mb-0 text-overflow">{{ lead.formFields.name.value }}</p>
            <p class="text-small mb-0 text-overflow">
              {{ lead.formFields.email.value }}
            </p>
            <p class="text-small mb-0">
              <em>{{ formatDate(lead.createdOn) }}</em>
            </p>
          </div>
        </div>
        <router-link
          :to="{
            name: 'campaignLeads',
            params: { campaignId: selectedProject._id },
          }"
        >
          <ak-button color="primary">View {{ leadsBrandingPlural }}</ak-button>
        </router-link>
      </div>
      <div :class="size === 'large' ? 'one-third' : 'one-whole mt'">
        <h6 class="color-grey">
          {{ leadsBrandingPlural }} Breakdown by Device
        </h6>
        <highcharts
          :options="chartOptions"
          class="one-half overflow-visible"
        ></highcharts>
        <em class="text-small"
          >Stats don't include {{ leadsBrandingPlural }} captured Offline</em
        >
        <h6 class="color-grey mt">Second Part Forms Complete</h6>
        <p v-if="overview.multipartConversion === null" class="text-small">
          No extra form parts completed
        </p>
        <p v-if="overview.multipartConversion !== null" class="text-huge">
          {{ overview.multipartConversion }}%
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, watch, ref } from 'vue'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import date from '@akerolabs/akero-vue/src/utils/date'

stockInit(Highcharts)

export default defineComponent({
  name: 'AkProjectBreakdown',

  components: {
    [Card.name]: Card,
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Box.name]: Box,
    highcharts: Chart,
  },
  props: {
    selectedProject: {
      default: () => ({}),
      type: Object,
    },
    statsSummary: {
      default: () => ({}),
      type: Object,
    },
    projectOverview: {
      default: () => ({}),
      type: Object,
    },
    size: {
      default: '',
      type: String,
    },
    loading: {
      default: true,
      type: Boolean,
    },
  },
  setup(props) {
    const overview = ref([])
    const leadsAvailable = ref(false)
    const leadsLoaded = ref(false)

    const chartOptions = ref({
      chart: {
        type: 'pie',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        height: 200,
        width: 200,
      },
      title: {
        text: null,
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f} ({point.y})</b>',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          innerSize: '50%',
          showInLegend: true,
          colors: ['#9941F1', '#FF00E5'],
          dataLabels: {
            enabled: false,
          },
        },
      },
      legend: {
        floating: true,
        x: 100,
        y: 0,
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
        borderWidth: 0,
        navigation: {
          enabled: true,
        },
      },
      credits: {
        enabled: false,
      },
      loading: {
        style: {
          opacity: 1,
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: 'Device Type',
          colorByPoint: true,
          data: [
            {
              name: 'Mobile',
              y: 0,
            },
            {
              name: 'Desktop',
              y: 0,
            },
          ],
        },
      ],
    })

    /**
     * @description User defined lead branding (plural)
     */
    const leadsBrandingPlural = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })
    /**
     * @description User defined lead branding (single)
     */
    const leadsBrandingSingle = computed(() => {
      return entityBranding('leads.singular', 'Lead')
    })

    // Format Created On Date
    const formatDate = (dateString) => {
      return date(dateString).fromNow()
    }

    const formatDateSansAgo = (dateString) => {
      return date(dateString).fromNow(true)
    }

    const indexedDayToName = (index) => {
      const days = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ]
      return days[index]
    }

    const hourToRange = (value) => {
      var start = value <= 9 ? '0' + value : value
      var end = value + 1 <= 9 ? '0' + (value + 1) : value + 1
      return start + ':00 - ' + end + ':00 UTC'
    }

    const buildData = () => {
      let chartData = [
        {
          name: 'Mobile',
          y: overview.value.mobileLeads || 0,
        },
        {
          name: 'Desktop',
          y: overview.value.desktopLeads || 0,
        },
      ]
      chartOptions.value.series[0].data = chartData
    }

    watch(
      () => props.projectOverview,
      (value) => {
        overview.value = value
        leadsAvailable.value = value.totalLeads > 0
        buildData()
      },
      { deep: true },
    )

    watch(
      () => props.loading,
      (value) => {
        leadsLoaded.value = value
      },
    )

    return {
      leadsBrandingPlural,
      leadsBrandingSingle,
      leadsAvailable,
      leadsLoaded,
      overview,
      formatDate,
      formatDateSansAgo,
      indexedDayToName,
      hourToRange,
      chartOptions,
    }
  },
})
</script>
